import { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";
import { ArrowLeft, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface OCWidgetPageProps {
  title: string;
  description: string;
  metaDescription: string;
  children: React.ReactNode;
}

const OCWidgetPage = ({ title, description, metaDescription, children }: OCWidgetPageProps) => {
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const existing = document.querySelector('script[src*="apis.owenscorning.com"]');
    if (!existing) {
      const script = document.createElement("script");
      script.src = "https://apis.owenscorning.com/client/widget.js?lang=en-us";
      script.async = true;
      document.body.appendChild(script);
    }

    // Watch for the OC script to inject iframes into the container
    const check = setInterval(() => {
      if (containerRef.current) {
        const iframe = containerRef.current.querySelector("iframe");
        if (iframe) {
          setLoading(false);
          clearInterval(check);
        }
      }
    }, 500);

    // Fallback: hide loading after 10s
    const fallback = setTimeout(() => {
      setLoading(false);
      clearInterval(check);
    }, 10000);

    return () => {
      clearInterval(check);
      clearTimeout(fallback);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>{title} | Zenith Solutions</title>
        <meta name="description" content={metaDescription} />
      </Helmet>
      <Navbar />
      <div className="pt-24 pb-20 min-h-screen bg-background">
        <div className="container mx-auto max-w-6xl px-4">
          <Link to="/tools" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" /> All Tools
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-8">
            <h1 className="text-2xl md:text-3xl font-black text-foreground mb-2">{title}</h1>
            <p className="text-muted-foreground">{description}</p>
          </motion.div>

          <div ref={containerRef} className="relative min-h-[400px]">
            <AnimatePresence>
              {loading && (
                <motion.div
                  key="loader"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10"
                >
                  <Loader2 className="h-10 w-10 text-primary animate-spin" />
                  <p className="text-sm text-muted-foreground font-medium">Loading tool…</p>
                </motion.div>
              )}
            </AnimatePresence>
            <div className={loading ? "opacity-0" : "opacity-100 transition-opacity duration-500"}>
              {children}
            </div>
          </div>
        </div>
      </div>
      <FooterSection />
    </>
  );
};

export default OCWidgetPage;
